import React from 'react'
import styled from 'styled-components'
import { Formik, FormikHelpers, FormikProps, useField } from 'formik'
import * as yup from 'yup'

// Constants
import {
  SKETCH_WEBSITE,
  professionalRoles,
  ProfessionalRole,
} from '@sketch/constants'

// Components
import { Button, Checkbox, Link, Form, Input } from '@sketch/components'
import {
  CreatePasswordInput,
  passwordStrengthValidation,
} from 'components/PasswordInput'
import { Select, SelectOption } from 'components/Select'

// Utils
import { trackEvent } from 'modules/analytics'
import { useTrackEditedFields } from './useTrackEditedFields'

// Constants
import {
  SIGN_UP_INITIAL_VALUES,
  TERMS_OF_SERVICE_PRIVACY_POLICY_ERROR,
  Values,
} from './constants'

// Hooks
import { useSignUpMetadata } from 'modules/user/hooks/useSignUpMetadata'

// Types
import {
  createPersonalAuthorization,
  setUserAuthorization,
} from 'graphql/multisession'
import {
  useSignUpMutation,
  GetInitialUserDocument,
  GetInitialUserQuery,
  SignUpMutationVariables,
} from '@sketch/gql-types'
import { ParsedError } from '@sketch/graphql-apollo/useMutation'

const signUpSchema = yup.object().shape({
  name: yup.string().trim().required('Enter your first name'),
  professionalRole: yup.mixed().required('Select your role'),
  email: yup
    .string()
    .email('This is not a valid email')
    .required('Enter your email address'),
  password: yup
    .string()
    .min(8, 'Should be 8 or more characters')
    .max(72, 'Should be 72 or less characters')
    .required('Create a new password')
    .test(
      'passwordStrength',
      'The password strength should be at least Okay',
      passwordStrengthValidation
    ),
  'terms-of-service': yup.boolean(),
  'privacy-policy': yup.boolean(),
  'marketing-consent': yup.boolean(),
})

export interface ExtendedFormikProps extends FormikProps<Values> {
  apiError: ParsedError | undefined
  onRoleChange: (role: ProfessionalRole) => void
  callToAction: string
}

const StyledCheckbox = styled(Checkbox)`
  /*
  Prevent the TOS link from being "blocked" by the input.
  */
  input {
    width: auto;
  }
`

const educationRoles: ProfessionalRole[] = [
  'STUDENT',
  'TEACHER',
  'EDUCATIONAL_INSTITUTION',
]
const isEducationRole = (role: ProfessionalRole): boolean =>
  educationRoles.includes(role)

interface RoleFieldProps
  extends Pick<
    ExtendedFormikProps,
    | 'values'
    | 'errors'
    | 'touched'
    | 'handleChange'
    | 'handleBlur'
    | 'onRoleChange'
  > {}

const RoleField: React.FC<RoleFieldProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  onRoleChange,
}) => {
  const [
    professionalRoleField,
    professionalRoleMeta,
    { setValue: setRole },
  ] = useField<ProfessionalRole>({
    name: 'professionalRole',
  })

  const onChange = (value: ProfessionalRole) => {
    onRoleChange(value)
    return setRole(value)
  }

  return (
    <Form.Field
      name="professionalRole"
      label="What do you do?"
      help={
        isEducationRole(professionalRoleField.value) && (
          <>
            Use Sketch for free with an{' '}
            <Link
              variant="primary"
              isUnderlined
              external
              href={`${SKETCH_WEBSITE}/store/education`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Education Workspace ↗
            </Link>
          </>
        )
      }
      errorText={
        touched.professionalRole ? professionalRoleMeta.error : undefined
      }
    >
      <Select
        {...professionalRoleField}
        placeholder="I am a…"
        onChange={onChange}
      >
        {Object.entries(professionalRoles).map(([value, name]) => (
          <SelectOption key={value} value={value}>
            {name}
          </SelectOption>
        ))}
      </Select>
    </Form.Field>
  )
}

type FieldsProps = ExtendedFormikProps & {
  isInShareInviteContext?: boolean
}

export const Fields: React.FC<FieldsProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  onRoleChange,
  /**
   * Fields can be rendered as part of the main sign up form
   * but also as part of a sign up form when invited to a share.
   */
  isInShareInviteContext = false,
}) => {
  useTrackEditedFields(values, isInShareInviteContext)
  const formContext = isInShareInviteContext ? 'share invite' : 'main'

  return (
    <>
      <RoleField
        values={values}
        errors={errors}
        touched={touched}
        handleChange={handleChange}
        handleBlur={handleBlur}
        onRoleChange={onRoleChange}
      />
      <Form.Field
        name="name"
        label="Your first name"
        errorText={touched.name ? errors.name : undefined}
      >
        <Input
          type="text"
          name="name"
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </Form.Field>
      <Form.Field
        name="email"
        label="Email"
        errorText={touched.email ? errors.email : undefined}
      >
        <Input
          name="email"
          type="email"
          value={values.email}
          onChange={handleChange}
          onBlur={handleBlur}
          autoFocus={!!values.email}
        />
      </Form.Field>
      <CreatePasswordInput
        name="password"
        value={values.password}
        onChange={handleChange}
        onBlur={handleBlur}
        invalid={touched.password && !!errors.password}
      />
      <Form.Field name="terms-of-service" mt={2} mb={3}>
        <StyledCheckbox
          name="terms-of-service"
          variant="untinted"
          checked={Boolean(values['terms-of-service'])}
          onChange={handleChange}
          onBlur={handleBlur}
        >
          I have read and agree to the&nbsp;
          <Link
            variant="primary"
            isUnderlined
            external
            href={`${SKETCH_WEBSITE}/tos`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackEvent('SIGN UP - "Terms of Service" link opened', {
                formContext,
              })
            }}
          >
            Terms of Service
          </Link>
        </StyledCheckbox>
      </Form.Field>
      <Form.Field name="privacy-policy" mt={2} mb={3}>
        <StyledCheckbox
          name="privacy-policy"
          variant="untinted"
          checked={Boolean(values['privacy-policy'])}
          onChange={handleChange}
          onBlur={handleBlur}
        >
          I have read and agree to the&nbsp;
          <Link
            variant="primary"
            isUnderlined
            external
            href={`${SKETCH_WEBSITE}/privacy`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackEvent('SIGN UP - "Privacy Policy" link opened', {
                formContext,
              })
            }}
          >
            Privacy Policy
          </Link>
        </StyledCheckbox>
      </Form.Field>
      <Form.Field name="marketing-consent" mb={8}>
        <StyledCheckbox
          name="marketing-consent"
          variant="untinted"
          checked={Boolean(values['marketing-consent'])}
          onChange={handleChange}
          onBlur={handleBlur}
        >
          Keep me updated on news and offers
        </StyledCheckbox>
      </Form.Field>
    </>
  )
}

export const Errors: React.FC<ExtendedFormikProps> = ({ apiError, status }) =>
  status || apiError ? (
    <Form.ErrorField>{status || apiError?.message || ''}</Form.ErrorField>
  ) : null

export const DefaultFormFields: React.FC<ExtendedFormikProps> = formProps => (
  <>
    <Fields {...formProps} />
    <Errors {...formProps} />
    <Submit
      disabled={!(formProps.isValid && formProps.dirty)}
      callToAction={formProps.callToAction}
      isSubmitting={formProps.isSubmitting}
      onClick={() => {
        const submitCount = formProps.submitCount + 1
        trackEvent('SIGN UP - Sign up form submitted', {
          submitCount: submitCount,
          formContext: 'main',
        })
      }}
    />
  </>
)
export interface SignUpFormProps {
  email?: string
  professionalRole?: SignUpMutationVariables['input']['professionalRole']
  /** User has an invitation token */
  invitedUser?: boolean
  children?: (formikProps: ExtendedFormikProps) => React.ReactNode
  onCompleted?: () => void
  onError?: (message: string) => void
  onRoleChange?: (role: ProfessionalRole) => void
  callToAction?: string
}

export interface SubmitProps {
  isSubmitting: boolean
  callToAction?: string
  disabled?: boolean
  onClick?: () => void
}

export const Submit: React.FC<SubmitProps> = ({
  isSubmitting,
  disabled,
  onClick,
  callToAction = 'Sign Up',
}) => (
  <Form.Footer>
    <Button
      variant="primary"
      type="submit"
      fill
      size="48"
      disabled={isSubmitting || disabled}
      loading={isSubmitting}
      onClick={onClick}
    >
      {callToAction}
    </Button>
  </Form.Footer>
)

const SignUpForm: React.FC<SignUpFormProps> = ({
  email = '',
  professionalRole,
  invitedUser,
  onCompleted,
  onError,
  children = DefaultFormFields,
  onRoleChange = () => {},
  callToAction = 'Sign Up',
}) => {
  const { metadata, clearMetadata: cleanSignUpMetadata } = useSignUpMetadata()

  const [signUp, { loading, error: apiError }] = useSignUpMutation({
    redirectErrors: true,
    update: (cache, { data }) => {
      if (!data?.signUp) {
        return
      }

      /* Save the user credentials in the apollo cache */
      setUserAuthorization(
        cache,
        createPersonalAuthorization(data.signUp!.credentials!)
      )

      /* Save the user profile information */
      cache.writeQuery<GetInitialUserQuery>({
        query: GetInitialUserDocument,
        data: {
          __typename: 'RootQueryType',
          me: data.signUp.user!,
        },
      })
    },
    onCompleted: () => {
      cleanSignUpMetadata()
      onCompleted?.()
    },
    onError: ({ message }) => {
      onError?.(message)
    },
  })

  const handleFormSubmit = async (
    values: Values,
    actions: FormikHelpers<Values>
  ) => {
    try {
      if (!values['terms-of-service'] || !values['privacy-policy']) {
        actions.setStatus(TERMS_OF_SERVICE_PRIVACY_POLICY_ERROR)
        return
      }

      actions.setStatus()

      const {
        'privacy-policy': privacyPolicyAgreed,
        'terms-of-service': tosAgreed,
        'marketing-consent': marketingConsent,
        ...otherValues
      } = values

      await signUp({
        variables: {
          input: {
            ...otherValues,
            privacyPolicyAgreed,
            tosAgreed,
            marketingConsent,
            createWorkspace: !invitedUser,
            metadata,
          },
        },
      })
    } catch (e) {
      // The error handling should be taken care of by Apollo
    } finally {
      actions.setSubmitting(false)
    }
  }

  return (
    <>
      <Formik
        initialValues={{
          ...SIGN_UP_INITIAL_VALUES,
          email,
          ...(professionalRole && { professionalRole }),
        }}
        onSubmit={handleFormSubmit}
        validationSchema={signUpSchema}
        validateOnBlur={false}
        enableReinitialize
      >
        {formikbag => (
          <Form>
            {children({
              ...formikbag,
              isSubmitting: loading,
              apiError,
              onRoleChange,
              callToAction,
            })}
          </Form>
        )}
      </Formik>
    </>
  )
}

export default SignUpForm
